import { FormEvent, useEffect, useMemo, useState } from 'react'

import { ImageData } from '@/requests/signup'
import { dataUrlToBlob } from '@/utils/file'
import { FileUploader } from './FileUploader'

type UploadFileProps = {
  onConfirm: (fileData: ImageData) => void
  isUploading: boolean
}

const isImagePreview = (fileData: ImageData) =>
  fileData.type.startsWith('image/')

export const UploadFileComp = ({ onConfirm, isUploading }: UploadFileProps) => {
  const [error, setError] = useState('')
  const [fileData, setFileData] = useState<ImageData | null>(null)

  const documentPreviewUrl = useMemo(
    () =>
      fileData && !isImagePreview(fileData)
        ? URL.createObjectURL(dataUrlToBlob(fileData.url))
        : null,
    [fileData]
  )

  useEffect(
    () => () => {
      if (documentPreviewUrl) {
        URL.revokeObjectURL(documentPreviewUrl)
      }
    },
    [documentPreviewUrl]
  )

  const handleSuccess = (fileData: ImageData) => {
    setFileData(fileData)
    setError('')
  }

  const handleConfirm = (e: FormEvent) => {
    e.preventDefault()
    if (fileData && !isUploading) {
      onConfirm(fileData)
    }
  }

  return (
    <form
      className="flex flex-col gap-5"
      onSubmit={handleConfirm}
      data-testid="form-upload-file"
    >
      {error && (
        <div className="text-rose-600" data-testid="error-upload-file-message">
          {error}
        </div>
      )}
      {fileData && (
        <div
          className="flex justify-center"
          data-testid="container-upload-file-preview"
        >
          {isImagePreview(fileData) ? (
            <img
              src={fileData.url}
              width={100}
              data-testid="img-upload-file-preview"
            />
          ) : (
            documentPreviewUrl && (
              <a
                href={documentPreviewUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="underline text-center break-all"
                data-testid="link-upload-file-preview"
              >
                {fileData.name}
              </a>
            )
          )}
        </div>
      )}
      <div
        className="flex justify-center mb-8"
        style={{ width: '100%' }}
        data-testid="container-upload-file-uploader"
      >
        <div style={{ width: '50%' }}>
          <FileUploader
            onUploadComplete={handleSuccess}
            onError={setError}
            customButtonText="Choose File"
            showUploadIcon={false}
            showUploadSVG={true}
          />
        </div>
      </div>
      <a
        className={`bg-black text-white dark:bg-semiWhite dark:text-black py-8 px-4 border-black w-full border-2 items-center text-center flex justify-center ${
          isUploading ? 'opacity-50 cursor-not-allowed' : ''
        }`}
        onClick={(e) => {
          e.preventDefault()
          if (!fileData || !!error || isUploading) {
            return
          }
          handleConfirm(e)
        }}
        role="button"
        aria-disabled={!fileData || !!error || isUploading}
        href="#"
        data-testid="btn-upload-file-submit"
      >
        {isUploading ? 'UPLOADING...' : 'SUBMIT'}
      </a>
    </form>
  )
}
